/**
 * videoEmbed.js
 * Convierte URLs de YouTube/Vimeo/MP4 a su representación HTML correcta.
 * YouTube y Vimeo necesitan <iframe>; los MP4 directos usan <video>.
 */

/**
 * Detecta el tipo de URL y devuelve { type, embedUrl }
 * @param {string} url
 * @returns {{ type: 'youtube'|'vimeo'|'mp4'|'unknown', embedUrl: string }}
 */
export function parseVideoUrl(url) {
    if (!url || url === 'placeholder') return { type: 'unknown', embedUrl: '' };

    // YouTube: youtu.be/ID  |  youtube.com/watch?v=ID  |  youtube.com/shorts/ID
    const ytMatch = url.match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/
    );
    if (ytMatch) {
        return {
            type: 'youtube',
            // autoplay=1 mute=1 loop=1 controls=0 para usarlo como fondo
            embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${ytMatch[1]}&controls=0&modestbranding=1&rel=0&playsinline=1`
        };
    }

    // Vimeo: vimeo.com/ID  |  vimeo.com/video/ID
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
        return {
            type: 'vimeo',
            embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&background=1`
        };
    }

    // MP4 / WebM directo
    if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url) || url.startsWith('data:video')) {
        return { type: 'mp4', embedUrl: url };
    }

    // Base64 de video (subida local)
    if (url.startsWith('data:video')) {
        return { type: 'mp4', embedUrl: url };
    }

    // Fallback: intentar como mp4 directo
    return { type: 'mp4', embedUrl: url };
}

/**
 * Renderiza el elemento correcto (iframe o video) según la URL.
 * Para uso como fondo de pantalla completa (Hero / Video sección).
 * @param {string} url
 * @param {string} extraStyle  estilos extra para el elemento raíz
 * @returns {string} HTML
 */
export function renderVideoBackground(url, extraStyle = '') {
    const { type, embedUrl } = parseVideoUrl(url);
    if (!embedUrl) return '';

    const baseStyle = `position:absolute;inset:0;width:100%;height:100%;z-index:0;`;

    if (type === 'youtube' || type === 'vimeo') {
        // Envolvemos el iframe en un contenedor con overflow:hidden
        // El iframe se escala al 130% para que los bordes con los controles
        // (título de YT arriba, barra de YT abajo) queden fuera del clip.
        return `<div style="${baseStyle} overflow:hidden; pointer-events:none;">
            <iframe
                src="${embedUrl}"
                style="position:absolute; top:50%; left:50%; width:130%; height:130%; transform:translate(-50%,-50%); border:none; pointer-events:none;"
                frameborder="0"
                allow="autoplay; fullscreen"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
                loading="lazy"
            ></iframe>
        </div>`;
    }

    // MP4 nativo
    return `<video autoplay muted loop playsinline preload="none"
        style="${baseStyle} object-fit:cover; opacity:0.6;"
    >
        <source src="${embedUrl}" type="video/mp4">
    </video>`;
}

/**
 * Renderiza el elemento para la variante LATERAL (split hero v3).
 * El iframe/video queda enmarcado como un card visual.
 * @param {string} url
 * @returns {string} HTML
 */
export function renderVideoCard(url) {
    const { type, embedUrl } = parseVideoUrl(url);
    if (!embedUrl) return '';

    const cardStyle = `width:100%; border-radius:30px; box-shadow:0 30px 60px rgba(0,0,0,0.2); aspect-ratio:16/9; overflow:hidden; display:block;`;

    if (type === 'youtube' || type === 'vimeo') {
        return `<div style="${cardStyle}">
            <iframe
                src="${embedUrl}"
                style="width:100%;height:100%;border:none;display:block;"
                frameborder="0"
                allow="autoplay; fullscreen"
                allowfullscreen
                loading="lazy"
            ></iframe>
        </div>`;
    }

    return `<video autoplay muted loop playsinline preload="none"
        style="width:100%; border-radius:30px; box-shadow:0 30px 60px rgba(0,0,0,0.2); object-fit:cover; aspect-ratio:16/9; display:block;"
    >
        <source src="${embedUrl}" type="video/mp4">
    </video>`;
}
